import { FC, RefObject } from 'react';
import classNames from 'classnames';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import Select, { Option, Value } from '@/components/ui/Select/Select.tsx';
import breakpoints from '@/constants/breakpoints.ts';
import styles from './TripEmissionLog.module.scss';
import Co2Period = App.Enums.Co2Period;
import DataComplianceUserItineraryFilterBy = App.Enums.DataComplianceUserItineraryFilterBy;

interface ActionNeededTableControlsProps {
	selectedRelationship: DataComplianceUserItineraryFilterBy | null;
	period: Co2Period;
	periodOptions: Option[];
	bookerTabRef: RefObject<HTMLButtonElement | null>;
	travelerTabRef: RefObject<HTMLButtonElement | null>;
	hasBookerTrips: boolean;
	hasTravelerTrips: boolean;
	relationshipTotalsReady: boolean;
	relationshipTabsLoading: boolean;
	indicatorLeft: number;
	indicatorWidth: number;
	onRelationshipChange: (relationship: DataComplianceUserItineraryFilterBy) => void;
	onPeriodChange: (period: Value) => void;
}

const ActionNeededTableControls: FC<ActionNeededTableControlsProps> = ({
	selectedRelationship,
	period,
	periodOptions,
	bookerTabRef,
	travelerTabRef,
	hasBookerTrips,
	hasTravelerTrips,
	relationshipTotalsReady,
	relationshipTabsLoading,
	indicatorLeft,
	indicatorWidth,
	onRelationshipChange,
	onPeriodChange,
}) => {
	const showActiveIndicator =
		selectedRelationship &&
		(!relationshipTotalsReady ||
			(selectedRelationship === 'booker' && hasBookerTrips) ||
			(selectedRelationship === 'traveler' && hasTravelerTrips));

	return (
		<FlexBlock
			className={styles.tableControls}
			justifyContent="space-between"
			alignItems={{
				[breakpoints.zero]: 'stretch',
				[breakpoints.tabletSmall]: 'flex-end',
			}}
			flexDirection={{
				[breakpoints.zero]: 'column-reverse',
				[breakpoints.tabletSmall]: 'row',
			}}
			rowGap="12px"
		>
			<FlexBlock className={styles.relationshipTabs} role="tablist" aria-label="Trip relationship">
				<button
					ref={bookerTabRef}
					type="button"
					className={classNames(
						selectedRelationship === 'booker' &&
							(!relationshipTotalsReady || hasBookerTrips) &&
							styles.active,
					)}
					disabled={relationshipTabsLoading || !hasBookerTrips}
					onClick={() => onRelationshipChange('booker')}
					role="tab"
					aria-selected={selectedRelationship === 'booker'}
				>
					Trips you arranged
				</button>
				<button
					ref={travelerTabRef}
					type="button"
					className={classNames(
						selectedRelationship === 'traveler' &&
							(!relationshipTotalsReady || hasTravelerTrips) &&
							styles.active,
					)}
					disabled={relationshipTabsLoading || !hasTravelerTrips}
					onClick={() => onRelationshipChange('traveler')}
					role="tab"
					aria-selected={selectedRelationship === 'traveler'}
				>
					Trips you took
				</button>
				{showActiveIndicator && (
					<span
						className={styles.activeIndicator}
						style={{
							transform: `translateX(${indicatorLeft}px)`,
							width: `${indicatorWidth}px`,
						}}
					/>
				)}
			</FlexBlock>
			<FlexBlock className={styles.periodControls} alignItems="center">
				<Select
					required
					ariaLabel="Filter action-needed trips by period"
					value={period}
					onChange={onPeriodChange}
					placeholder="Select period"
					name="period"
					options={periodOptions}
					className={styles.periodSelect}
				/>
			</FlexBlock>
		</FlexBlock>
	);
};

export default ActionNeededTableControls;
